/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-17
 */

// Add third-party dependencies.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
} from '@mui/material';

// Add local dependencies.
import Header from '../../components/layout/Header';
import StudioSelectorControl from '../../components/common/StudioSelectorControl';
import NoToolAccess from '../../components/common/NoToolAccess';
import MasterItemsManagement from './components/MasterItemsManagement';
import ChecklistMessagesManagement from './components/ChecklistMessagesManagement';
import ResponsesManagement from './components/ResponsesManagement';
import { useOwnerManagerAccess } from '../../hooks/useToolAccess';
import { useSessionAuth } from '../../hooks/useSessionAuth';

/**
 * Class Essentials page component.
 * Main page for managing class essentials - master items catalog,
 * sending checklists to parents, and parent responses.
 *
 * @returns {React.ReactElement} The class essentials page component
 *
 * @example
 * return (
 *   <ClassEssentialsPage />
 * );
 */
const ClassEssentialsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get authentication state
  const { isAuthenticated, isLoading: authLoading } = useSessionAuth();

  // Get studio access information (Owner/Manager only)
  const {
    eligibleStudios,
    hasToolAccess,
    hasSingleStudio,
    isLoading: toolAccessLoading,
  } = useOwnerManagerAccess();

  // Component state
  const [selectedStudioId, setSelectedStudioId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Handle studio selection and access control
  useEffect(() => {
    // Wait for authentication and tool access data to load
    if (authLoading || toolAccessLoading) {
      return;
    }

    // If user is not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Auto-select if user has exactly one eligible studio
    if (hasSingleStudio) {
      const [studio] = eligibleStudios;
      setSelectedStudioId(studio.id);
    }
  }, [
    navigate,
    authLoading,
    isAuthenticated,
    toolAccessLoading,
    hasSingleStudio,
    eligibleStudios,
  ]);

  // Show loading state
  if (authLoading || toolAccessLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show no access message if user doesn't have required permissions
  if (!hasToolAccess) {
    return (
      <NoToolAccess
        toolName="Class Essentials"
        requiredRoles={['owner', 'manager']}
      />
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 1, sm: 2, md: 3 },
          pb: isMobile ? 10 : 3,
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: { xs: 1.5, sm: 2, md: 3 } }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
            }}
          >
            Class Essentials
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
              lineHeight: 1.4,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Send parents personalized checklists of required items for their
            student&apos;s classes.
          </Typography>
        </Box>

        {/* Studio Selector - Only shows for multiple studios */}
        <Box mb={3}>
          <StudioSelectorControl
            studios={eligibleStudios}
            selectedStudioId={selectedStudioId}
            onStudioChange={setSelectedStudioId}
          />
        </Box>

        {/* Info Alert for Multiple Studios */}
        {eligibleStudios.length > 1 && !selectedStudioId && (
          <Box mb={3}>
            <Alert severity="info" sx={{ maxWidth: 400 }}>
              Please select a studio to manage class essentials.
            </Alert>
          </Box>
        )}

        {/* Content area - only show if studio is selected */}
        {selectedStudioId && (
          <Box>
            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
              >
                <Tab label="Master Items" />
                <Tab label="Messages" />
                <Tab label="Responses" />
              </Tabs>
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
              <MasterItemsManagement studioId={selectedStudioId} />
            )}
            {activeTab === 1 && (
              <ChecklistMessagesManagement studioId={selectedStudioId} />
            )}
            {activeTab === 2 && (
              <ResponsesManagement studioId={selectedStudioId} />
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ClassEssentialsPage;
